import { FC, ReactNode, MouseEvent } from 'react';
import { Controller, ControllerProps } from 'react-hook-form';
import { Box, IconButton, Tooltip } from '@mui/material';
import { ITextEditorProps, TextEditor } from '../../textEditor/textEditor.component.tsx';

export const FormTextEditorWithButton: FC<TFormTextEditorWithButton> = ({
  defaultValue,
  onBlur,
  onChange,
  defaultFormat,
  name,
  controllerProps = {},

  buttonIcon,
  buttonAriaLabel = 'action',
  buttonTooltip,
  onButtonClick,
  buttonVisibility = 'always',
  buttonDisabled,

  ...editorProps
}) => {
  const containerClass = 'FormTextEditorWithButton-root';

  return (
    <Controller
      name={name}
      defaultValue={defaultValue}
      {...controllerProps}
      render={({
        field: { value, onBlur: onFieldBlur, onChange: onFieldChange, ref: _ref, ...fieldProps },
      }) => {
        const visibilitySx = getVisibilitySx(buttonVisibility, `.${containerClass}`);

        const actionButton = (
          <IconButton
            className='FormTextEditorWithButton-action'
            size='small'
            aria-label={buttonVisibility === 'never' ? '' : buttonAriaLabel}
            onClick={onButtonClick}
            disabled={
              Boolean(buttonDisabled) ||
              Boolean(editorProps?.disabled) ||
              buttonVisibility === 'never'
            }
            sx={{
              p: 0.5,
              position: 'absolute',
              top: 2,
              right: 6,
              zIndex: 2,
              ...visibilitySx,
            }}
          >
            {buttonIcon}
          </IconButton>
        );

        return (
          <Box className={containerClass} sx={{ position: 'relative' }}>
            {buttonTooltip ? (
              <Tooltip title={buttonTooltip} arrow>
                {actionButton}
              </Tooltip>
            ) : (
              actionButton
            )}

            <TextEditor
              defaultValue={defaultValue}
              value={value}
              onBlur={(e) => {
                onFieldBlur();
                onBlur?.(e);
              }}
              onChange={(e) => {
                onFieldChange(e);
                onChange?.(e);
              }}
              defaultFormat={defaultFormat}
              {...fieldProps}
              {...editorProps}
              isUseForm
            />
          </Box>
        );
      }}
    />
  );
};

export interface TFormTextEditorWithButton extends ITextEditorProps {
  controllerProps?: Omit<ControllerProps, 'render' | 'name'>;
  name: ControllerProps['name'];
  buttonIcon?: ReactNode;
  buttonAriaLabel?: string;
  buttonTooltip?: string;
  onButtonClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  buttonVisibility?: 'always' | 'hover' | 'focus' | 'hoverOrFocus' | 'never';
  buttonDisabled?: boolean;
}

function getVisibilitySx(
  mode: TFormTextEditorWithButton['buttonVisibility'],
  parentSelector: string,
): Record<string, any> {
  const focusVisible = {
    '&:focus': { opacity: 1 },
    '&.Mui-focusVisible': { opacity: 1 },
  } as const;

  const base = { transition: 'opacity 120ms ease' } as const;
  switch (mode) {
    case 'always':
      return { ...base, opacity: 1 };
    case 'never':
      return { ...base, opacity: 0 };
    case 'hover':
      return {
        ...base,
        opacity: 0,
        [`${parentSelector}:hover &`]: { opacity: 1 },
        ...focusVisible,
      };
    case 'focus':
      return {
        ...base,
        opacity: 0,
        [`${parentSelector}:focus-within &`]: { opacity: 1 },
        ...focusVisible,
      };
    case 'hoverOrFocus':
      return {
        ...base,
        opacity: 0,
        [`${parentSelector}:hover &, ${parentSelector}:focus-within &`]: { opacity: 1 },
        ...focusVisible,
      };
    default:
      return { ...base, opacity: 1 };
  }
}
